import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Search, 
  Plus, 
  Calendar, 
  Clock, 
  MapPin, 
  Users,
  Shield,
  Target,
  Send,
  CheckCircle,
  XCircle,
  Loader2,
  Trophy,
  Zap,
  Coins
} from "lucide-react";

const LEVEL_NAMES = {
  1: "Bronz",
  2: "Gümüş",
  3: "Altın",
  4: "Platin",
  5: "Elmas"
};

const LEVEL_COLORS = {
  1: "bg-amber-700",
  2: "bg-gray-400",
  3: "bg-yellow-500",
  4: "bg-cyan-400",
  5: "bg-purple-500",
};

const DAY_NAMES = {
  monday: "Pazartesi",
  tuesday: "Salı",
  wednesday: "Çarşamba",
  thursday: "Perşembe",
  friday: "Cuma",
  saturday: "Cumartesi",
  sunday: "Pazar"
};

const RivalAdsPage = ({ user, onLogout, refreshUser }) => {
  const navigate = useNavigate();
  const [ads, setAds] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [autoMatch, setAutoMatch] = useState(null);
  const [potentialMatches, setPotentialMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("browse");
  
  // Create ad dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAd, setNewAd] = useState({
    matchDate: "",
    matchTime: "20:00",
    venuePreference: "neutral",
    preferredVenue: "",
    format: "7x7",
    description: ""
  });

  // Auto match dialog
  const [showAutoMatchDialog, setShowAutoMatchDialog] = useState(false);
  const [autoMatchData, setAutoMatchData] = useState({
    preferredDays: [],
    preferredTimes: ["19:00", "20:00", "21:00"],
    format: "7x7",
    levelRange: 1,
    venuePreference: "any"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [adsRes, myAdsRes, autoMatchRes] = await Promise.all([
        axios.get(`${API}/rival-ads`),
        axios.get(`${API}/rival-ads/my-club`).catch(() => ({ data: [] })),
        axios.get(`${API}/auto-match/registration`).catch(() => ({ data: null }))
      ]);
      
      setAds(adsRes.data);
      setMyAds(myAdsRes.data);
      setAutoMatch(autoMatchRes.data);
      
      // Eğer otomatik eşleşme aktifse, potansiyel eşleşmeleri getir
      if (autoMatchRes.data?.isActive) {
        const matchesRes = await axios.get(`${API}/auto-match/find`);
        setPotentialMatches(matchesRes.data.matches || []);
      }
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAd = async () => {
    if (!newAd.matchDate || !newAd.matchTime) {
      toast.error("Tarih ve saat zorunlu");
      return;
    }
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/rival-ads`, newAd);
      toast.success(`İlan oluşturuldu! (${response.data.newCredits || 0} kredi kaldı)`);
      setShowCreateDialog(false);
      setNewAd({
        matchDate: "",
        matchTime: "20:00",
        venuePreference: "neutral",
        preferredVenue: "",
        format: "7x7",
        description: ""
      });
      fetchData();
      // Kredi bilgisini güncelle
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İlan oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  const handleRespondToAd = async (adId) => {
    try {
      const response = await axios.post(`${API}/rival-ads/${adId}/respond`, { adId });
      toast.success(`Başvurunuz gönderildi! (${response.data.newCredits || 0} kredi kaldı)`);
      fetchData();
      // Kredi bilgisini güncelle
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Başvuru gönderilemedi");
    }
  };

  const handleAcceptResponse = async (adId, clubId) => {
    try {
      await axios.post(`${API}/rival-ads/${adId}/accept/${clubId}`);
      toast.success("Eşleşme tamamlandı!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleCancelAd = async (adId) => {
    try {
      await axios.delete(`${API}/rival-ads/${adId}`);
      toast.success("İlan iptal edildi");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const handleSaveAutoMatch = async () => {
    if (autoMatchData.preferredDays.length === 0) {
      toast.error("En az bir gün seçin");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/auto-match/register`, autoMatchData);
      toast.success(response.data.message || "Otomatik eşleşme ayarları kaydedildi!");
      setShowAutoMatchDialog(false);
      fetchData();
      // Kredi bilgisini güncelle
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kayıt başarısız");
    }
  };

  const handleSendQuickChallenge = async (match) => {
    try {
      const response = await axios.post(`${API}/matches/quick-challenge/${match.club.id}`, {
        commonDays: match.commonDays,
        commonTimes: match.commonTimes
      });
      toast.success(`${match.club.name} kulübüne maç teklifi gönderildi! (${response.data.newCredits} kredi kaldı)`);
      fetchData();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Teklif gönderilemedi");
    }
  };

  const handleToggleAutoMatch = async () => {
    try {
      const response = await axios.post(`${API}/auto-match/toggle`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    }
  };

  const toggleDay = (day) => {
    setAutoMatchData(prev => ({
      ...prev,
      preferredDays: prev.preferredDays.includes(day)
        ? prev.preferredDays.filter(d => d !== day)
        : [...prev.preferredDays, day]
    }));
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600 dark:text-emerald-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 dark:from-dark-card dark:to-dark-accent text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Target className="h-8 w-8" />
                Rakip Bul
              </h1>
              <p className="text-orange-100 dark:text-gray-300 mt-1">
                Kulübün için rakip ara veya ilan oluştur
              </p>
            </div>
            
            {user?.clubId && (
              <div className="flex flex-wrap gap-2 items-center">
                {/* Kredi Bilgisi */}
                <div className="bg-white/20 rounded-lg px-4 py-2 flex items-center gap-2">
                  <span>Krediniz:</span>
                  <span className="font-bold text-lg">{user?.credits || 0} 💰</span>
                </div>
                <Button 
                  variant="secondary"
                  onClick={() => setShowAutoMatchDialog(true)}
                  disabled={(user?.credits || 0) < 5 && !autoMatch}
                >
                  <Zap className="h-4 w-4 mr-2" />
                  {autoMatch ? "Ayarları Düzenle" : "Otomatik Eşleş (5💰)"}
                </Button>
                <Button 
                  onClick={() => setShowCreateDialog(true)}
                  className="bg-white text-orange-600 hover:bg-orange-50"
                  disabled={(user?.credits || 0) < 5}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  İlan Oluştur (5💰)
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {!user?.clubId && (
          <Card className="mb-6 bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <Shield className="h-6 w-6 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Kulübe Üye Ol</p>
                  <p className="text-sm text-amber-600">Rakip ilanı oluşturmak veya başvurmak için bir kulübe üye olmalısınız</p>
                </div>
                <Button onClick={() => navigate("/clubs")} className="ml-auto bg-amber-600 hover:bg-amber-700">
                  Kulüplere Git
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="browse">
              <Search className="h-4 w-4 mr-2" />
              İlanlar ({ads.length})
            </TabsTrigger>
            <TabsTrigger value="my-ads">
              <Trophy className="h-4 w-4 mr-2" />
              İlanlarım ({myAds.length})
            </TabsTrigger>
            <TabsTrigger value="auto-match">
              <Zap className="h-4 w-4 mr-2" />
              Otomatik Eşleşme
            </TabsTrigger>
          </TabsList>

          {/* Browse Ads Tab */}
          <TabsContent value="browse">
            {ads.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Target className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500">Henüz aktif ilan yok</p>
                  {user?.clubId && (
                    <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" />
                      İlk İlanı Oluştur
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {ads.map((ad) => {
                  // Logo kontrolü - base64 ise resim, değilse emoji
                  const isBase64Logo = ad.clubLogo && ad.clubLogo.startsWith('data:');
                  const logoEmoji = !ad.clubLogo || isBase64Logo ? "⚽" : ad.clubLogo;
                  
                  return (
                  <Card key={ad.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-2xl overflow-hidden">
                          {isBase64Logo ? (
                            <img src={ad.clubLogo} alt={ad.clubName} className="w-full h-full object-cover" />
                          ) : (
                            logoEmoji
                          )}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{ad.clubName}</CardTitle>
                          <div className="flex gap-2">
                            <Badge className={`${LEVEL_COLORS[ad.clubLevel]} text-white text-xs`}>
                              {LEVEL_NAMES[ad.clubLevel]}
                            </Badge>
                            {ad.clubCity && (
                              <Badge variant="outline" className="text-xs">
                                <MapPin className="h-3 w-3 mr-1" />
                                {ad.clubCity}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span>{formatDate(ad.matchDate)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span>{ad.matchTime}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-gray-500" />
                          <span>{ad.format}</span>
                        </div>
                        {ad.venuePreference && (
                          <div className="flex items-center gap-2 text-sm">
                            <MapPin className="h-4 w-4 text-gray-500" />
                            <span>
                              {ad.venuePreference === "home" && "Ev sahibi"}
                              {ad.venuePreference === "away" && "Deplasman"}
                              {ad.venuePreference === "neutral" && "Nötr saha"}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {ad.description && (
                        <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                          {ad.description}
                        </p>
                      )}
                      
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {ad.responses?.length || 0} başvuru
                        </span>
                        {user?.clubId && user.clubId !== ad.clubId && (
                          <Button 
                            size="sm"
                            onClick={() => handleRespondToAd(ad.id)}
                            disabled={ad.responses?.includes(user.clubId) || (user?.credits || 0) < 5}
                          >
                            {ad.responses?.includes(user.clubId) ? (
                              <>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Başvuruldu
                              </>
                            ) : (
                              <>
                                <Send className="h-4 w-4 mr-1" />
                                Başvur (5💰)
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* My Ads Tab */}
          <TabsContent value="my-ads">
            {!user?.clubId ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500">Kulübe üye olmalısınız</p>
                </CardContent>
              </Card>
            ) : myAds.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Trophy className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500">Henüz ilan oluşturmadınız</p>
                  <Button onClick={() => setShowCreateDialog(true)} className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    İlan Oluştur
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myAds.map((ad) => (
                  <Card key={ad.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {formatDate(ad.matchDate)} - {ad.matchTime}
                            <Badge variant={ad.status === "active" ? "default" : ad.status === "matched" ? "success" : "secondary"}>
                              {ad.status === "active" && "Aktif"}
                              {ad.status === "matched" && "Eşleşti"}
                              {ad.status === "cancelled" && "İptal"}
                            </Badge>
                          </CardTitle>
                          <CardDescription>
                            {ad.format} • {ad.responses?.length || 0} başvuru
                          </CardDescription>
                        </div>
                        {ad.status === "active" && (
                          <Button variant="outline" size="sm" onClick={() => handleCancelAd(ad.id)}>
                            <XCircle className="h-4 w-4 mr-1" />
                            İptal
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    {ad.status === "active" && ad.respondingClubs?.length > 0 && (
                      <CardContent>
                        <p className="text-sm font-medium mb-3">Başvuranlar:</p>
                        <div className="space-y-2">
                          {ad.respondingClubs.map((club) => (
                            <div key={club.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                                  {club.logoUrl || "⚽"}
                                </div>
                                <div>
                                  <p className="font-medium">{club.name}</p>
                                  <div className="flex gap-2">
                                    <Badge className={`${LEVEL_COLORS[club.level]} text-white text-xs`}>
                                      {LEVEL_NAMES[club.level]}
                                    </Badge>
                                    {club.city && (
                                      <span className="text-xs text-gray-500">{club.city}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button size="sm" onClick={() => handleAcceptResponse(ad.id, club.id)}>
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Kabul Et
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Auto Match Tab */}
          <TabsContent value="auto-match">
            {!user?.clubId ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Shield className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500">Kulübe üye olmalısınız</p>
                </CardContent>
              </Card>
            ) : !autoMatch ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Zap className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 mb-4">Otomatik eşleşme sistemine kayıtlı değilsiniz</p>
                  <Button onClick={() => setShowAutoMatchDialog(true)}>
                    <Zap className="h-4 w-4 mr-2" />
                    Kayıt Ol
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Auto Match Status */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Otomatik Eşleşme</CardTitle>
                        <CardDescription>
                          Tercihlerinize göre otomatik rakip bulma
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={autoMatch.isActive ? "default" : "secondary"}>
                          {autoMatch.isActive ? "Aktif" : "Pasif"}
                        </Badge>
                        <Button variant="outline" size="sm" onClick={handleToggleAutoMatch}>
                          {autoMatch.isActive ? "Durdur" : "Başlat"}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowAutoMatchDialog(true)}>
                          Düzenle
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Tercih Edilen Günler</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {autoMatch.preferredDays?.map(day => (
                            <Badge key={day} variant="outline">{DAY_NAMES[day]}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Tercih Edilen Saatler</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {autoMatch.preferredTimes?.map(time => (
                            <Badge key={time} variant="outline">{time}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Format & Seviye Farkı</p>
                        <p className="font-medium">{autoMatch.format} • ±{autoMatch.levelRange} seviye</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Potential Matches */}
                {autoMatch.isActive && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Potansiyel Eşleşmeler</CardTitle>
                      <CardDescription>
                        Tercihlerinize uyan kulüpler
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {potentialMatches.length === 0 ? (
                        <p className="text-center text-gray-500 py-4">
                          Şu an uygun eşleşme bulunamadı
                        </p>
                      ) : (
                        <div className="space-y-3">
                          {potentialMatches.map((match, idx) => (
                            <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                              <div className="flex-1">
                                <p className="font-medium">{match.club.name}</p>
                                <div className="flex gap-2 mt-1">
                                  <Badge className={`${LEVEL_COLORS[match.club.level]} text-white text-xs`}>
                                    {LEVEL_NAMES[match.club.level]}
                                  </Badge>
                                  {match.club.city && (
                                    <span className="text-xs text-gray-500">{match.club.city}</span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  Ortak günler: {match.commonDays.map(d => DAY_NAMES[d]).join(", ")}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => navigate(`/clubs/${match.club.id}`)}
                                >
                                  Görüntüle
                                </Button>
                                <Button 
                                  size="sm"
                                  className="bg-emerald-500 hover:bg-emerald-600"
                                  onClick={() => handleSendQuickChallenge(match)}
                                  disabled={(user?.credits || 0) < 20}
                                >
                                  <Send className="h-3 w-3 mr-1" />
                                  Teklif (20💰)
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Ad Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rakip İlanı Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Maç Tarihi</Label>
                <Input
                  type="date"
                  value={newAd.matchDate}
                  onChange={(e) => setNewAd({ ...newAd, matchDate: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
              <div className="space-y-2">
                <Label>Maç Saati</Label>
                <Select value={newAd.matchTime} onValueChange={(v) => setNewAd({ ...newAd, matchTime: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["18:00", "19:00", "20:00", "21:00", "22:00"].map(time => (
                      <SelectItem key={time} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Format</Label>
                <Select value={newAd.format} onValueChange={(v) => setNewAd({ ...newAd, format: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5x5">5x5</SelectItem>
                    <SelectItem value="6x6">6x6</SelectItem>
                    <SelectItem value="7x7">7x7</SelectItem>
                    <SelectItem value="8x8">8x8</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Saha Tercihi</Label>
                <Select value={newAd.venuePreference} onValueChange={(v) => setNewAd({ ...newAd, venuePreference: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">Ev Sahibi</SelectItem>
                    <SelectItem value="away">Deplasman</SelectItem>
                    <SelectItem value="neutral">Nötr Saha</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Açıklama (Opsiyonel)</Label>
              <Textarea
                placeholder="Maç hakkında ek bilgi..."
                value={newAd.description}
                onChange={(e) => setNewAd({ ...newAd, description: e.target.value })}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>İptal</Button>
            <Button onClick={handleCreateAd} disabled={creating}>
              {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Auto Match Dialog */}
      <Dialog open={showAutoMatchDialog} onOpenChange={setShowAutoMatchDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Otomatik Eşleşme Ayarları</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tercih Edilen Günler</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(DAY_NAMES).map(([key, name]) => (
                  <Button
                    key={key}
                    variant={autoMatchData.preferredDays.includes(key) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleDay(key)}
                  >
                    {name}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Format</Label>
                <Select value={autoMatchData.format} onValueChange={(v) => setAutoMatchData({ ...autoMatchData, format: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5x5">5x5</SelectItem>
                    <SelectItem value="6x6">6x6</SelectItem>
                    <SelectItem value="7x7">7x7</SelectItem>
                    <SelectItem value="8x8">8x8</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Kabul Edilebilir Seviye Farkı</Label>
                <Select value={String(autoMatchData.levelRange)} onValueChange={(v) => setAutoMatchData({ ...autoMatchData, levelRange: parseInt(v) })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Sadece aynı seviye</SelectItem>
                    <SelectItem value="1">±1 seviye</SelectItem>
                    <SelectItem value="2">±2 seviye</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Saha Tercihi</Label>
              <Select value={autoMatchData.venuePreference} onValueChange={(v) => setAutoMatchData({ ...autoMatchData, venuePreference: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Farketmez</SelectItem>
                  <SelectItem value="home">Ev Sahibi</SelectItem>
                  <SelectItem value="away">Deplasman</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAutoMatchDialog(false)}>İptal</Button>
            <Button onClick={handleSaveAutoMatch}>
              <Zap className="h-4 w-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RivalAdsPage;
